// EditCaseModal.jsx - Fully Fixed & Modern (Same Pattern as Transfer Modal)
import React, { useState, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import LocationDropdown from '../../../tabs/Dropdown/LocationName';
import axiosInstance from '../../../../../api';

const EditCaseModal = ({ show, onClose, caseId, refreshList }) => {
  const [formData, setFormData] = useState({
    case_name: '',
    case_location: '',
    case_area: '',
    patient_location: '',
    patient_area: '',
    accident_place: '',
    nearest_medical_center: '',
    injury_type: '',
    age: '',
    gender: '',
    contact_number: '',
    triage_card: '',
    bracelet_id: '',
    documents: {}
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  // Dropdown data
  const [locations, setLocations] = useState([]);
  const [allAreas, setAllAreas] = useState([]);
  const [areas, setAreas] = useState([]);
  const [patientAreas, setPatientAreas] = useState([]);
  const [medicalCenters, setMedicalCenters] = useState([]);
  const [fileInputs, setFileInputs] = useState([0]);

  const roleName = sessionStorage.getItem('Role_Name') || '';
  const isEMSRequester = roleName.toLowerCase() === 'ems requester';
  const userLocationId = sessionStorage.getItem('User_Location_Id');

  const MAX_FILES = 3;
  const injuryTypes = ['Fracture', 'Laceration', 'Burn', 'Sprain', 'Strain', 'Concussion', 'Dislocation', 'Abrasion', 'Contusion', 'Puncture Wound', 'Amputation', 'Crush Injury'];

  // Fetch full case data when modal opens
  useEffect(() => {
    if (!show || !caseId) return;

    const fetchCase = async () => {
      setIsLoading(true);
      try {
        const authToken = sessionStorage.getItem('auth_token');
        const res = await axiosInstance.get(`/ems/cases/${caseId}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });

        const data = res.data.data || res.data;

        setFormData({
          case_name: data.case_name || '',
          case_location: String(data.case_location || ''),
          case_area: String(data.case_area || ''),
          patient_location: String(data.patient_location || ''),
          patient_area: String(data.patient_area || ''),
          accident_place: data.accident_place || '',
          nearest_medical_center: String(data.nearest_medical_center || ''),
          injury_type: data.injury_type || '',
          age: data.age || '',
          gender: data.gender || '',
          contact_number: data.contact_number || '',
          triage_card: data.triage_card || '',
          bracelet_id: data.bracelet_id || '',
          documents: {}
        });

        // Trigger area & medical center loading
        if (data.case_area) fetchMedicalCenters(data.case_area);
      } catch (err) {
        console.error('Failed to load case:', err);
        alert('Could not load case details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCase();
  }, [show, caseId]);

  // Auto-set location for EMS Requester
  useEffect(() => {
    if (show && isEMSRequester && userLocationId && !formData.case_location) {
      setFormData(prev => ({ ...prev, case_location: userLocationId }));
    }
  }, [show, isEMSRequester, userLocationId]);

  // Fetch locations & areas
  useEffect(() => {
    if (!show) return;

    const fetchData = async () => {
      try {
        const [locRes, areaRes] = await Promise.all([
          axiosInstance.get('/ems/locations'),
          axiosInstance.get('/ems/areas')
        ]);
        setLocations(locRes.data || []);
        setAllAreas(areaRes.data || []);
      } catch (err) {
        console.error('Failed to load dropdowns', err);
      }
    };
    fetchData();
  }, [show]);

  // Update areas when location changes
  useEffect(() => {
    if (!formData.case_location) {
      setAreas([]);
      return;
    }
    const filtered = allAreas.filter(a => String(a.location_id) === formData.case_location);
    setAreas(filtered);
  }, [formData.case_location, allAreas]);

  useEffect(() => {
    if (!formData.patient_location) {
      setPatientAreas([]);
      return;
    }
    const filtered = allAreas.filter(a => String(a.location_id) === formData.patient_location);
    setPatientAreas(filtered);
  }, [formData.patient_location, allAreas]);

  // Fetch medical centers when area changes
  const fetchMedicalCenters = async (areaId) => {
    try {
      const res = await axiosInstance.get(`/ems/get-nearby-medical-center-by-area/${areaId}`);
      setMedicalCenters(res.data || []);
    } catch (err) {
      console.error('Failed to load medical centers', err);
    }
  };

  useEffect(() => {
    if (formData.case_area) {
      fetchMedicalCenters(formData.case_area);
    }
  }, [formData.case_area]);

  // QR Scanner
  useEffect(() => {
    if (!showScanner) return;
    const scanner = new Html5QrcodeScanner('reader', { fps: 10, qrbox: 250 }, false);
    scanner.render(
      (text) => {
        setFormData(prev => ({ ...prev, bracelet_id: text }));
        setShowScanner(false);
        scanner.clear();
      },
      () => { }
    );
    return () => scanner.clear();
  }, [showScanner]);

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'case_name' || name === 'injury_type') {
      if (!/^[a-zA-Z\s]*$/.test(value)) return;
    }
    if (name === 'contact_number' && value && !/^\d*$/.test(value)) return;

    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/jfif'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, [`doc${index + 1}`]: 'Invalid file type' }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      documents: { ...prev.documents, [`documents${index + 1}`]: file }
    }));
  };

  const addFileInput = () => fileInputs.length < MAX_FILES && setFileInputs([...fileInputs, fileInputs.length]);
  const removeFileInput = (index) => {
    setFileInputs(fileInputs.filter((_, i) => i !== index));
    setFormData(prev => {
      const { [`documents${index + 1}`]: _, ...rest } = prev.documents;
      return { ...prev, documents: rest };
    });
  };

  const validate = () => {
    const err = {};
    if (!formData.case_name.trim()) err.case_name = 'Required';
    if (!formData.case_location) err.case_location = 'Required';
    if (!formData.case_area) err.case_area = 'Required';
    if (!formData.patient_location) err.patient_location = 'Required';
    if (formData.patient_location && !formData.patient_area) err.patient_area = 'Required';
    if (!formData.accident_place?.trim()) err.accident_place = 'Required';
    if (!formData.nearest_medical_center) err.nearest_medical_center = 'Required';
    if (!formData.injury_type?.trim()) err.injury_type = 'Required';
    if (!formData.triage_card) err.triage_card = 'Required';
    if (!formData.bracelet_id?.trim()) err.bracelet_id = 'Required';

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    const payload = new FormData();
    payload.append('case_name', formData.case_name);
    payload.append('case_location', formData.case_location);
    payload.append('case_area', formData.case_area);
    payload.append('patient_location', formData.patient_location);
    payload.append('patient_area', formData.patient_area || '');
    payload.append('accident_place', formData.accident_place);
    payload.append('nearest_medical_center', formData.nearest_medical_center);
    payload.append('injury_type', formData.injury_type);
    payload.append('age', formData.age || '');
    payload.append('gender', formData.gender || '');
    payload.append('contact_number', formData.contact_number || '');
    payload.append('triage_card', formData.triage_card);
    payload.append('bracelet_id', formData.bracelet_id);
    payload.append('_method', 'PUT');

    Object.entries(formData.documents).forEach(([key, file]) => {
      if (file instanceof File) payload.append(key, file);
    });

    try {
      await axiosInstance.post(`/ems/cases/${caseId}`, payload);
      alert('Case updated successfully!');
      refreshList();
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit EMS Case</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>

            <div className="modal-body">
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary"></div>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label>Case Name <span className="text-danger">*</span></label>
                      <input type="text" name="case_name" className={`form-control ${errors.case_name ? 'is-invalid' : ''}`} value={formData.case_name} onChange={handleChange} />
                      {errors.case_name && <div className="invalid-feedback">{errors.case_name}</div>}
                    </div>

                    <div className="col-md-6">
                      <label>Location <span className="text-danger">*</span></label>
                      {isEMSRequester ? (
                        <select className="form-select" value={formData.case_location} disabled>
                          <option>{locations.find(l => l.id == formData.case_location)?.location_name || 'Loading...'}</option>
                        </select>
                      ) : (
                        <LocationDropdown value={formData.case_location} onChange={(e) => setFormData(prev => ({ ...prev, case_location: e.target.value }))} />
                      )}
                    </div>

                    <div className="col-md-6">
                      <label>Area <span className="text-danger">*</span></label>
                      <select className={`form-select ${errors.case_area ? 'is-invalid' : ''}`} value={formData.case_area} onChange={(e) => setFormData(prev => ({ ...prev, case_area: e.target.value }))}>
                        <option value="">Select Area</option>
                        {areas.map(a => <option key={a.id} value={a.id}>{a.area_name}</option>)}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label>Patient Location <span className="text-danger">*</span></label>
                      <LocationDropdown value={formData.patient_location} onChange={(e) => setFormData(prev => ({ ...prev, patient_location: e.target.value }))} />
                    </div>

                    <div className="col-md-6">
                      <label>Patient Area {formData.patient_location && <span className="text-danger">*</span>}</label>
                      <select className="form-select" value={formData.patient_area} onChange={(e) => setFormData(prev => ({ ...prev, patient_area: e.target.value }))}>
                        <option value="">Select Area</option>
                        {patientAreas.map(a => <option key={a.id} value={a.id}>{a.area_name}</option>)}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label>Accident Place <span className="text-danger">*</span></label>
                      <input type="text" className={`form-control ${errors.accident_place ? 'is-invalid' : ''}`} value={formData.accident_place} onChange={(e) => setFormData(prev => ({ ...prev, accident_place: e.target.value }))} />
                    </div>

                    <div className="col-md-6">
                      <label>Transfer To <span className="text-danger">*</span></label>
                      <select className={`form-select ${errors.nearest_medical_center ? 'is-invalid' : ''}`} value={formData.nearest_medical_center} onChange={(e) => setFormData(prev => ({ ...prev, nearest_medical_center: e.target.value }))}>
                        <option value="">Select Medical Center</option>
                        {medicalCenters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label>Type of Injury <span className="text-danger">*</span></label>
                      <input type="text" list="injuryTypes" className={`form-control ${errors.injury_type ? 'is-invalid' : ''}`} name="injury_type" value={formData.injury_type} onChange={handleChange} />
                      <datalist id="injuryTypes">
                        {injuryTypes.map(t => <option key={t} value={t} />)}
                      </datalist>
                    </div>

                    <div className="col-md-3">
                      <label>Age</label>
                      <input type="number" className="form-control" value={formData.age} onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))} min="0" max="120" />
                    </div>

                    <div className="col-md-3">
                      <label>Gender</label>
                      <select className="form-select" value={formData.gender} onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}>
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label>Contact Number</label>
                      <input type="text" className="form-control" value={formData.contact_number} onChange={handleChange} name="contact_number" />
                    </div>

                    <div className="col-md-6">
                      <label>Triage Card <span className="text-danger">*</span></label>
                      <select className={`form-select ${errors.triage_card ? 'is-invalid' : ''}`} value={formData.triage_card} onChange={(e) => setFormData(prev => ({ ...prev, triage_card: e.target.value }))}>
                        <option value="">Select</option>
                        <option value="Red">Red</option>
                        <option value="Yellow">Yellow</option>
                        <option value="Green">Green</option>
                        <option value="Black">Black</option>
                      </select>
                    </div>

                    {/* Bracelet ID */}
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label>Bracelet ID <span className="text-danger">*</span></label>
                        <div className="d-flex align-items-center">
                          <input
                            type="text"
                            className={`form-control ${errors.bracelet_id ? 'is-invalid' : ''}`}
                            name="bracelet_id"
                            value={formData.bracelet_id || ''}
                            onChange={handleChange}
                            required
                          />
                          <i
                            className="ti ti-scan text-primary fs-4 ms-3 d-lg-none d-block"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setShowScanner(true)}
                          ></i>
                        </div>
                        {errors.bracelet_id && <div className="invalid-feedback">{errors.bracelet_id}</div>}
                      </div>
                    </div>

                    {/* Document Uploads */}
                    <div className="row">
                      <div className="col-md-12">
                        <label>Documents Upload</label>
                        <p className="text-muted small mb-2">
                          Allowed file types: PDF, JPG, JPEG, PNG, WEBP, JFIF. Maximum 3 files.
                        </p>
                        {fileInputs.map((_, index) => (
                          <div key={index} className="mb-3 d-flex align-items-center flex-nowrap">
                            <div className="flex-grow-1 me-2">
                              <input
                                className={`form-control ${errors[`documents${index + 1}`] ? 'is-invalid' : ''}`}
                                type="file"
                                onChange={(e) => handleFileChange(e, index)}
                                accept="image/jpeg,image/jpg,image/png,image/webp,image/jfif,application/pdf"
                              />
                              {errors[`documents${index + 1}`] && (
                                <div className="invalid-feedback">{errors[`documents${index + 1}`]}</div>
                              )}
                            </div>
                            <div className="d-flex align-items-center">
                              {index === fileInputs.length - 1 && fileInputs.length < MAX_FILES && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon fs-5 me-2"
                                  onClick={addFileInput}
                                >
                                  <i className="ti ti-circle-plus text-success"></i>
                                </button>
                              )}
                              {fileInputs.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-sm fs-5 btn-icon"
                                  onClick={() => removeFileInput(index)}
                                >
                                  <i className="ti ti-trash text-danger"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer mt-4">
                    <button type="button" className="btn btn-outline-danger" onClick={onClose} disabled={isLoading}>Cancel</button>
                    <button type="submit" className="btn btn-outline-primary" disabled={isLoading}>
                      {isLoading ? 'Updating...' : 'Update Case'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {showScanner && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.9)', zIndex: 9999 }}>
          <div className="text-end p-3">
            <button className="btn-close btn-close-white" onClick={() => setShowScanner(false)}></button>
          </div>
          <div className="d-flex justify-content-center">
            <div id="reader" style={{ width: '90%', maxWidth: '500px' }}></div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditCaseModal;